import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, GeoJSON, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Landmark, Upload, Trash2, FileUp, Ruler, PenTool, Download, ShieldCheck, Pencil, Save, X, Trees, MapPinned } from 'lucide-react';
import {
  uploadProjectBoundary, deleteProjectBoundary, createFellingArea, updateFellingArea, deleteFellingArea,
  importFellingAreas, exportProjectMap, downloadBlob
} from '../../services/api';
import { STATUS_PIN_COLORS } from '../ProjectsLeafletMap';
import { useMapLayers } from '../../hooks/useMapLayers';
import { WmsLayers, LayerControlPanel } from './WmsLayers';
import { MapLayerSettingsDialog } from './MapLayerSettingsDialog';
import { DrawControl, FellingAreasLayer, PreviewGeoJsonLayer, FELLING_COLOR } from './FellingAreasLayer';
import { FellingAreaDialog } from './FellingAreaDialog';
import { VmdAffirmationLookup } from './VmdAffirmationLookup';

const centerIcon = (color) => L.divIcon({
  className: 'sila-pin',
  html: `<svg width="26" height="36" viewBox="0 0 30 42" style="filter:drop-shadow(0 2px 2px rgba(0,0,0,.35))"><path d="M15 0C6.7 0 0 6.7 0 15c0 10 15 27 15 27s15-17 15-27C30 6.7 23.3 0 15 0z" fill="${color}" stroke="#fff" stroke-width="2"/><circle cx="15" cy="15" r="6" fill="#fff"/></svg>`,
  iconSize: [26, 36],
  iconAnchor: [13, 36]
});

const FitAll = ({ boundary, areas, marker, fitKey }) => {
  const map = useMap();
  useEffect(() => {
    const bounds = L.latLngBounds([]);
    if (boundary) bounds.extend(L.geoJSON(boundary).getBounds());
    areas.forEach((a) => { if (a.geometry) bounds.extend(L.geoJSON(a.geometry).getBounds()); });
    if (marker) bounds.extend(marker);
    if (bounds.isValid()) map.fitBounds(bounds, { padding: [24, 24], maxZoom: 16 });
    else map.setView([56.95, 24.6], 7);
  }, [fitKey, map]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
};

const SOURCE_LV = { drawn: 'iezīmēta', kml: 'imports', vmd: 'VMD' };

export const ProjectMapCard = ({ project, boundary, boundaryMeta, areas = [], canEdit = false, showLayers = false, onBoundaryChanged, reloadAreas, compact = false }) => {
  const [busy, setBusy] = useState(false);
  const [drawing, setDrawing] = useState(false);
  const [pendingGeometry, setPendingGeometry] = useState(null);
  const [editMetaArea, setEditMetaArea] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [pendingEdits, setPendingEdits] = useState({});
  const [selectedId, setSelectedId] = useState(null);
  const [showVmd, setShowVmd] = useState(false);
  const [vmdPreview, setVmdPreview] = useState(null);
  const [layerSettingsOpen, setLayerSettingsOpen] = useState(false);
  const [fitKey, setFitKey] = useState(0);
  const boundaryInput = useRef(null);
  const importInput = useRef(null);
  const mapLayers = useMapLayers(showLayers);
  const color = STATUS_PIN_COLORS[project?.status] || STATUS_PIN_COLORS.not_started;
  const hasCoords = project?.latitude != null && project?.longitude != null;
  const marker = useMemo(() => (hasCoords ? [project.latitude, project.longitude] : null), [hasCoords, project?.latitude, project?.longitude]);
  const loadedOnce = useRef(false);

  useEffect(() => {
    if (!loadedOnce.current && (boundary || areas.length || marker)) {
      loadedOnce.current = true;
      setFitKey((k) => k + 1);
    }
  }, [boundary, areas.length, marker]);

  const run = async (fn, successMessage) => {
    setBusy(true);
    try {
      const result = await fn();
      if (successMessage) toast.success(typeof successMessage === 'function' ? successMessage(result) : successMessage);
      return result;
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Darbība neizdevās');
      return null;
    } finally {
      setBusy(false);
    }
  };

  const uploadBoundary = async (file) => {
    if (!file) return;
    const result = await run(() => uploadProjectBoundary(project.id, file), (r) => `Robežas ielādētas: ${r.boundary_meta.filename}${r.boundary_meta.area_ha ? ` · ${r.boundary_meta.area_ha} ha` : ''}`);
    if (boundaryInput.current) boundaryInput.current.value = '';
    if (result) {
      onBoundaryChanged?.({ boundary_meta: result.boundary_meta, geojson: result.geojson, latitude: result.latitude, longitude: result.longitude, area_ha: result.area_ha });
      setFitKey((k) => k + 1);
    }
  };

  const removeBoundary = async () => {
    if (!window.confirm('Dzēst objekta robežas?')) return;
    const result = await run(() => deleteProjectBoundary(project.id), 'Robežas dzēstas');
    if (result) onBoundaryChanged?.({ boundary_meta: null, geojson: null });
  };

  const saveNewArea = async (form) => {
    const created = await run(() => createFellingArea(project.id, { ...form, geometry: pendingGeometry, source: 'drawn' }), (r) => `Cirsma saglabāta${r.area_ha ? ` · ${r.area_ha} ha` : ''}`);
    if (created) {
      setPendingGeometry(null);
      reloadAreas?.();
    }
  };

  const saveMeta = async (form) => {
    const updated = await run(() => updateFellingArea(editMetaArea.id, form), 'Cirsma atjaunināta');
    if (updated) {
      setEditMetaArea(null);
      reloadAreas?.();
    }
  };

  const saveGeometryEdit = async () => {
    const geometry = pendingEdits[editingId];
    if (!geometry) {
      setEditingId(null);
      return;
    }
    const updated = await run(() => updateFellingArea(editingId, { geometry }), (r) => `Kontūra saglabāta · ${r.area_ha ?? '–'} ha`);
    if (updated) {
      setPendingEdits((prev) => { const next = { ...prev }; delete next[editingId]; return next; });
      setEditingId(null);
      reloadAreas?.();
    }
  };

  const removeArea = async (area) => {
    if (!window.confirm(`Dzēst cirsmu “${area.name}”?`)) return;
    const result = await run(() => deleteFellingArea(area.id), 'Cirsma dzēsta');
    if (result) reloadAreas?.();
  };

  const importAreas = async (file) => {
    if (!file) return;
    const created = await run(() => importFellingAreas(project.id, file), (r) => `Importētas ${r.length} cirsmas`);
    if (importInput.current) importInput.current.value = '';
    if (created) {
      reloadAreas?.();
      setFitKey((k) => k + 1);
    }
  };

  const exportMap = async (format) => {
    const blob = await run(() => exportProjectMap(project.id, format));
    if (blob) downloadBlob(blob, `${(project.name || 'objekts').replace(/[^\p{L}\p{N}_-]+/gu, '_')}.${format}`);
  };

  const totalAreaHa = areas.reduce((sum, a) => sum + (a.area_ha || 0), 0);
  const nothingToShow = !boundary && !hasCoords && areas.length === 0;
  if (!canEdit && nothingToShow) return null;

  return (
    <Card className="border-0 shadow-sm" data-testid="project-map-card">
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <CardTitle className="text-lg flex items-center gap-2"><Landmark className="w-5 h-5 text-[#1F3D2B]" /> Karte: robežas un cirsmas</CardTitle>
            {boundaryMeta ? (
              <p className="text-xs text-[#2E2E2E]/60 flex flex-wrap gap-x-3 gap-y-1 mt-1" data-testid="project-boundary-meta">
                <span className="flex items-center gap-1"><FileUp className="w-3.5 h-3.5" /> {boundaryMeta.filename}</span>
                {boundaryMeta.area_ha ? <span className="flex items-center gap-1"><Ruler className="w-3.5 h-3.5" /> Robežu platība: {boundaryMeta.area_ha} ha</span> : null}
                {areas.length > 0 && <span className="flex items-center gap-1 text-red-700"><Trees className="w-3.5 h-3.5" /> {areas.length} cirsmas · {Math.round(totalAreaHa * 100) / 100} ha</span>}
              </p>
            ) : (
              <p className="text-xs text-[#2E2E2E]/60 mt-1" data-testid="project-boundary-empty">
                {canEdit ? 'Nav ielādētu robežu. Ielādējiet KML/KMZ/GeoJSON — adatiņa un platība aizpildīsies automātiski.' : 'Objektam robežas vēl nav pievienotas.'}
                {areas.length > 0 && <span className="ml-2 text-red-700">{areas.length} cirsmas · {Math.round(totalAreaHa * 100) / 100} ha</span>}
              </p>
            )}
          </div>
          {canEdit && (
            <div className="flex flex-wrap items-center gap-1.5" data-testid="project-map-toolbar">
              <input ref={boundaryInput} type="file" accept=".kml,.kmz,.geojson,.json" className="hidden" onChange={(e) => uploadBoundary(e.target.files?.[0])} data-testid="project-boundary-file-input" />
              <input ref={importInput} type="file" accept=".kml,.kmz,.geojson,.json" className="hidden" onChange={(e) => importAreas(e.target.files?.[0])} data-testid="felling-import-input" />
              <Button size="sm" variant="outline" disabled={busy} onClick={() => boundaryInput.current?.click()} data-testid="project-boundary-upload">
                <Upload className="w-4 h-4 mr-1.5" /> {boundaryMeta ? 'Aizstāt robežas' : 'Ielādēt robežas'}
              </Button>
              {boundaryMeta && (
                <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700" disabled={busy} onClick={removeBoundary} title="Dzēst robežas" data-testid="project-boundary-delete"><Trash2 className="w-4 h-4" /></Button>
              )}
              <Button size="sm" onClick={() => { setDrawing((d) => !d); setEditingId(null); }} className={drawing ? 'bg-red-600 hover:bg-red-700' : 'bg-[#1F3D2B] hover:bg-[#2a5039]'} data-testid="felling-draw-toggle">
                <PenTool className="w-4 h-4 mr-1.5" /> {drawing ? 'Pārtraukt zīmēšanu' : 'Iezīmēt cirsmu'}
              </Button>
              <Button size="sm" variant="outline" disabled={busy} onClick={() => importInput.current?.click()} data-testid="felling-import">
                <FileUp className="w-4 h-4 mr-1.5" /> Importēt cirsmas
              </Button>
              <Button size="sm" variant="outline" onClick={() => setShowVmd((v) => !v)} className={showVmd ? 'border-blue-400 text-blue-800' : ''} data-testid="vmd-toggle">
                <ShieldCheck className="w-4 h-4 mr-1.5" /> VMD apliecinājums
              </Button>
            </div>
          )}
        </div>
        {(boundary || areas.length > 0) && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            <Button size="sm" variant="ghost" className="h-7 text-xs" disabled={busy} onClick={() => exportMap('kml')} data-testid="export-kml"><Download className="w-3.5 h-3.5 mr-1" /> Eksportēt KML</Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs" disabled={busy} onClick={() => exportMap('geojson')} data-testid="export-geojson"><Download className="w-3.5 h-3.5 mr-1" /> Eksportēt GeoJSON</Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setFitKey((k) => k + 1)} data-testid="map-fit"><MapPinned className="w-3.5 h-3.5 mr-1" /> Centrēt</Button>
          </div>
        )}
      </CardHeader>
      <CardContent className="pt-0 space-y-3">
        {showVmd && canEdit && (
          <VmdAffirmationLookup projectId={project.id} onPreview={setVmdPreview} onClose={() => setShowVmd(false)} onSaved={() => { reloadAreas?.(); setFitKey((k) => k + 1); }} />
        )}
        {drawing && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 flex items-center justify-between gap-2 animate-fade-in" data-testid="felling-draw-hint">
            <span>Klikšķiniet kartē, lai liktu cirsmas punktus. Noslēdziet kontūru, uzklikšķinot uz pirmā punkta.</span>
            <button type="button" onClick={() => setDrawing(false)} className="underline">Atcelt</button>
          </div>
        )}
        {editingId && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex flex-wrap items-center justify-between gap-2 animate-fade-in" data-testid="felling-edit-hint">
            <span>Kontūras labošana: velciet punktus kartē. {pendingEdits[editingId] ? 'Ir nesaglabātas izmaiņas.' : ''}</span>
            <span className="flex gap-2">
              <Button size="sm" className="h-7 bg-[#1F3D2B] hover:bg-[#2a5039]" onClick={saveGeometryEdit} disabled={busy} data-testid="felling-edit-save"><Save className="w-3.5 h-3.5 mr-1" /> Saglabāt kontūru</Button>
              <Button size="sm" variant="outline" className="h-7" onClick={() => { setEditingId(null); setPendingEdits({}); reloadAreas?.(); }} data-testid="felling-edit-cancel"><X className="w-3.5 h-3.5 mr-1" /> Atcelt</Button>
            </span>
          </div>
        )}
        <div className={`relative ${compact ? 'h-[320px]' : 'h-[440px]'} rounded-xl overflow-hidden border border-gray-100`} data-testid="project-boundary-map">
          <MapContainer center={marker || [56.95, 24.6]} zoom={marker ? 13 : 7} scrollWheelZoom={!compact} className="w-full h-full z-0">
            <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" />
            {showLayers && <WmsLayers activeLayers={mapLayers.activeLayers} />}
            <FitAll boundary={boundary} areas={areas} marker={marker} fitKey={fitKey} />
            {boundary && <GeoJSON key={`${project.id}-${boundaryMeta?.uploaded_at}`} data={boundary} style={() => ({ color, weight: 3, fillColor: color, fillOpacity: 0.12 })} />}
            <FellingAreasLayer areas={areas} selectedId={selectedId} onSelect={setSelectedId} editingId={editingId} canEdit={canEdit} onEdited={(id, geometry) => setPendingEdits((prev) => ({ ...prev, [id]: geometry }))} />
            <PreviewGeoJsonLayer geojson={vmdPreview} />
            {marker && <Marker position={marker} icon={centerIcon(color)} />}
            {canEdit && <DrawControl drawing={drawing} onCreated={(geometry) => { setDrawing(false); setPendingGeometry(geometry); }} />}
          </MapContainer>
          {showLayers && <LayerControlPanel mapLayers={mapLayers} canManage={canEdit} onManage={() => setLayerSettingsOpen(true)} />}
        </div>

        {areas.length > 0 && (
          <div className="rounded-xl border border-gray-100 divide-y" data-testid="felling-area-list">
            {areas.map((area) => (
              <div key={area.id} className={`flex flex-wrap items-center gap-2 px-3 py-2 text-sm transition-colors ${selectedId === area.id ? 'bg-red-50' : ''}`} data-testid={`felling-area-row-${area.id}`}>
                <button type="button" onClick={() => { setSelectedId(area.id); }} className="flex-1 min-w-[160px] text-left">
                  <span className="inline-block w-2.5 h-2.5 rounded-sm mr-2 align-middle" style={{ background: FELLING_COLOR }} />
                  <span className="font-medium" data-testid={`felling-area-name-${area.id}`}>{area.name}</span>
                  <span className="text-xs text-[#2E2E2E]/60 ml-2">
                    {area.affirmation_number ? `Nr. ${area.affirmation_number} · ` : ''}{area.area_ha ? `${area.area_ha} ha · ` : ''}{SOURCE_LV[area.source] || area.source}
                  </span>
                  {area.notes && <span className="block text-xs text-[#2E2E2E]/60 italic truncate">{area.notes}</span>}
                </button>
                {canEdit && (
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEditMetaArea(area)} data-testid={`felling-area-edit-${area.id}`}><Pencil className="w-3.5 h-3.5 mr-1" /> Dati</Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs" disabled={editingId && editingId !== area.id} onClick={() => { setDrawing(false); setSelectedId(area.id); setEditingId(editingId === area.id ? null : area.id); }} data-testid={`felling-area-edit-geometry-${area.id}`}>
                      <PenTool className="w-3.5 h-3.5 mr-1" /> {editingId === area.id ? 'Labo...' : 'Kontūra'}
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 text-red-600 hover:text-red-700" onClick={() => removeArea(area)} data-testid={`felling-area-delete-${area.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {canEdit && (
        <>
          <FellingAreaDialog open={!!pendingGeometry} onOpenChange={(open) => !open && setPendingGeometry(null)} geometry={pendingGeometry} onSave={saveNewArea} saving={busy} />
          <FellingAreaDialog open={!!editMetaArea} onOpenChange={(open) => !open && setEditMetaArea(null)} area={editMetaArea} onSave={saveMeta} saving={busy} />
          {showLayers && <MapLayerSettingsDialog open={layerSettingsOpen} onOpenChange={setLayerSettingsOpen} mapLayers={mapLayers} />}
        </>
      )}
    </Card>
  );
};

export default ProjectMapCard;
